import React, { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';

import { updateJobApplicant } from '../../action/application/update-job-applicant';
import { addApplicant } from '../../action/job/add-applicant';
import { createApplicationSource } from '../../application-source/actions/create-application-source';
import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { jobSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { loadSourceTypes } from '../../source-type/actions/load-source-types';
import { manualSourceTypesForOrgIdSelector } from '../../source-type/source-type-selectors';
import { loadJobApplicationCountsByStatus } from '../actions/load-job-application-counts-by-status';

import { uploadFileViaEmployer } from '../../api/file-upload-api-client';
import { normalizePhone } from '../../businesslogic/form-normalizers';
import { required, validateEmail, validatePhone } from '../../businesslogic/form-validators';
import { UiButton } from '../../ui/ui-button';
import { jobInProgressRoute, jobNeedReviewRoute } from '../../web-routes';

import { ACCEPTED_RESUME_TYPES } from '../../application/widgets/upload-resume';
import {
  ACCEPTED_STATUS,
  APPLIED_STATUS,
  INTERVIEWING_STATUS,
  PHONE_INTERVIEWING_STATUS,
  statusDisplay,
  VALIDATING_STATUS,
} from '../../config/application-status-config';
import { RESUMES_BUCKET } from '../../config/aws-config';
import { convertPhoneNumberToE164 } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { shouldUsePhoneInterview } from '../../util/should-use-phone-interview';
import { Checkbox, Select, TextField } from '../../widgets/redux-form-fields/material-ui-wrappers';

const mapStateToProps = (state: any, { orgId, jobId }: InputProps) => ({
  user: userSelector(state),
  job: jobSelector(state, jobId),
  org: orgSelector(state, orgId),
  sourceTypes: manualSourceTypesForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, jobId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    addApplicant: (applicant: NewManualApplicant) => dispatch(addApplicant(jobId, applicant)),
    updateJobApplicant: (
      jobApplicantId: number,
      update: {
        resumeUploadedByName: string;
        resumeUploadedBy: string;
        resumeUploadedAt: Moment;
        resumeFileName: string;
      },
    ) => dispatch(updateJobApplicant(jobApplicantId, orgId, update)),
    loadSourceTypes: () => dispatch(loadSourceTypes(orgId)),
    loadApplicationCounts: () => dispatch(loadJobApplicationCountsByStatus(jobId)),
    createApplicationSource: (applicationSource: ApplicationSourceCreateRequest) =>
      dispatch(createApplicationSource(applicationSource)),
  },
});

interface InputProps {
  jobId: number;
  orgId: number;
  onAddApplicant?: () => void;
  viewOnCreate?: boolean;
  manuallyAddingApplicant: boolean;
  setManuallyAddingApplicant: (addingApplicant: boolean) => () => void;
}

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

// redux-form has a very complicated type. Basically, the first parameter of the generic InjectedFormProps
// describes the values in the form, and the second parameter is the props that will be passed through to
// your component. You have to specify both and then in the call below, also specify both.
type ManuallyAddApplicantButtonComponentProps = InputProps & DispatchProps & ReduxProps;
type Props = InjectedFormProps<NewManualApplicant, ManuallyAddApplicantButtonComponentProps> &
  ManuallyAddApplicantButtonComponentProps;

export const ManuallyAddApplicantDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<NewManualApplicant, ManuallyAddApplicantButtonComponentProps>({
    form: 'ManualApplicantForm',
  })((props: Props) => {
    const {
      org,
      job,
      user,
      orgId,
      jobId,
      manuallyAddingApplicant,
      setManuallyAddingApplicant,
      viewOnCreate,
      sourceTypes,
      onAddApplicant,
      handleSubmit,
      actions,
    } = props;

    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [existingApplicant, setExistingApplicant] = useState<FullApplicant | null>(null);
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isAddingNewSource, setIsAddingNewSource] = useState<boolean>(false);

    const history = useHistory();

    useEffect(() => {
      actions.loadSourceTypes();
    });

    const closeDialog = () => {
      setManuallyAddingApplicant(false)();
      setExistingApplicant(null);
    };

    const handleAddApplicant = async (values: NewManualApplicant) => {
      setIsAdding(true);
      let result;

      /* handle creating new source type */
      if (isAddingNewSource && (values.newSourceName || '').trim().length !== 0) {
        const newSourceType = await actions.createApplicationSource({
          orgId,
          jobId,
          newSourceName: values.newSourceName.trim(),
          isManualSource: true,
          returnUrl: false,
        });
        result = await actions.addApplicant({
          ...values,
          phoneNumber: convertPhoneNumberToE164(values.phoneNumber),
          sourceType: newSourceType,
          wasManuallyAdded: true,
        });

        setIsAddingNewSource(false);
      } else {
        result = await actions.addApplicant({
          ...values,
          phoneNumber: convertPhoneNumberToE164(values.phoneNumber),
          wasManuallyAdded: true,
        });
      }

      await actions.loadApplicationCounts();

      if (result.existing) {
        setExistingApplicant(result.application);
        setManuallyAddingApplicant(false)();
      } else {
        const { job_applicant_id: jobApplicantId, job_application_hash: jobApplicationHash } =
          result.application;

        if (resumeFile && user.username) {
          const fileKey = `application/${jobApplicationHash}/${resumeFile.name}`;
          await uploadFileViaEmployer(fileKey, resumeFile, RESUMES_BUCKET, 'public-read');
          await actions.updateJobApplicant(jobApplicantId, {
            resumeUploadedByName: `${user.firstName} ${user.lastName}`,
            resumeUploadedBy: user.username,
            resumeUploadedAt: moment().utc(),
            resumeFileName: resumeFile.name,
          });
        }
        setIsAdding(false);
        if (viewOnCreate) {
          viewApplicant(jobApplicantId, values.status)();
        } else if (onAddApplicant) {
          onAddApplicant();
        } else {
          closeDialog();
        }
      }
    };

    const viewApplicant = (jobApplicantId: number, status: ApplicationStatus) => () => {
      closeDialog();
      if (status === APPLIED_STATUS) {
        history.push(jobNeedReviewRoute(orgId, jobId, { jobApplicantId }));
      } else {
        history.push(jobInProgressRoute(orgId, jobId, { status, jobApplicantId }));
      }
    };

    const handleFileUploadClick = async ({
      target: { files },
    }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files || files.length === 0) {
        return;
      }
      setResumeFile(files[0]);
    };

    const handleAddNewSourceToggle = () => {
      setIsAddingNewSource(!isAddingNewSource);
    };

    const renderApplicantForm = () => {
      const usePhoneInterview = shouldUsePhoneInterview(org, job);
      const statuses = [
        APPLIED_STATUS,
        ACCEPTED_STATUS,
        ...(usePhoneInterview ? [PHONE_INTERVIEWING_STATUS] : []),
        INTERVIEWING_STATUS,
        VALIDATING_STATUS,
      ];

      return (
        <>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              component={TextField}
              label="First Name"
              name="firstName"
              validate={[required]}
              className="u-width-100"
            />
          </FormControl>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              component={TextField}
              label="Last Name"
              name="lastName"
              validate={[required]}
              className="u-width-100"
            />
          </FormControl>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              component={TextField}
              label="Email"
              name="email"
              validate={[validateEmail]}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MaterialDesignIcon iconName={ICON_NAMES.MAIL} />
                  </InputAdornment>
                ),
              }}
              className="u-width-100"
            />
          </FormControl>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              component={TextField}
              label="Phone Number"
              name="phoneNumber"
              normalize={normalizePhone}
              validate={[validatePhone]}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MaterialDesignIcon iconName={ICON_NAMES.CALL} />
                  </InputAdornment>
                ),
              }}
              className="u-width-100"
            />
          </FormControl>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              name="status"
              validate={[required]}
              label="Status"
              component={Select}
              className="u-width-100"
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusDisplay(status)}
                </MenuItem>
              ))}
            </Field>
          </FormControl>
          {isAddingNewSource ? (
            <FormControl className="u-margin-y u-display-flex u-width-250px">
              <Field
                name="newSourceName"
                label="New Source"
                component={TextField}
                className="u-width-100"
              />
            </FormControl>
          ) : (
            <FormControl className="u-margin-y u-display-flex u-width-250px">
              <Field
                name="sourceType"
                label="Source Type"
                component={Select}
                className="u-width-100"
              >
                {sourceTypes.map((sourceType) => (
                  <MenuItem key={sourceType.source_type} value={sourceType.source_type}>
                    {sourceType.name}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          )}
          <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexStart">
            <div role="presentation" className="typo-link" onClick={handleAddNewSourceToggle}>
              {isAddingNewSource ? 'Or choose from existing sources' : 'Or create a custom source'}
            </div>
          </div>
          <div className="u-margin-y">
            <div className="typo-subtitle">Resume</div>
            <input type="file" accept={ACCEPTED_RESUME_TYPES} onChange={handleFileUploadClick} />
          </div>
          <FormControl className="u-margin-y u-display-flex u-width-250px">
            <Field
              component={Checkbox}
              label="Do you have permission to text this person?"
              name="textFollowups"
              className="u-width-100"
            />
          </FormControl>
        </>
      );
    };

    const renderExistingApplicant = () => {
      if (!existingApplicant) {
        return null;
      }

      return (
        <Typography variant="body1">
          We found an existing applicant for {existingApplicant.firstName}{' '}
          {existingApplicant.lastName} with the same email or phone number
        </Typography>
      );
    };

    return (
      (manuallyAddingApplicant || existingApplicant) && (
        <Dialog open onClose={closeDialog}>
          <form onSubmit={handleSubmit(handleAddApplicant)}>
            <DialogTitle>Add New Applicant</DialogTitle>
            <DialogContent>
              {manuallyAddingApplicant && renderApplicantForm()}
              {existingApplicant && renderExistingApplicant()}
            </DialogContent>
            <DialogActions>
              <UiButton onClick={closeDialog} variant="dismiss">
                {existingApplicant ? 'Close' : 'Cancel'}
              </UiButton>
              {existingApplicant ? (
                <UiButton
                  onClick={viewApplicant(
                    existingApplicant.job_applicant_id,
                    existingApplicant.status,
                  )}
                >
                  View Applicant
                </UiButton>
              ) : (
                <UiButton loading={isAdding} type="submit">
                  Add
                </UiButton>
              )}
            </DialogActions>
          </form>
        </Dialog>
      )
    );
  }),
);
